import React from 'react';
import { Compass, GraduationCap, Tag } from 'lucide-react';

interface RmRefItem {
  id?: string;
  title?: string;
  slug?: string;
  route?: string;
}

type FooterItem = string | RmRefItem;

interface RmContentFooterProps {
  pillar: FooterItem[];
  teacher: FooterItem[];
  tags: FooterItem[];
}

const getLabel = (item: FooterItem): string => {
  if (typeof item === 'string') return item;
  return item.title || item.slug || item.id || '';
};

interface PillRowProps {
  label: string;
  items: FooterItem[];
  icon: React.ElementType;
  pillClassName: string;
}

const PillRow: React.FC<PillRowProps> = ({ label, items, icon: Icon, pillClassName }) => {
  if (!items || items.length === 0) return null;
  const visible = items.filter((item) => getLabel(item));
  if (visible.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
      {visible.map((item, i) => (
        <span
          key={i}
          className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full border ${pillClassName}`}
        >
          {getLabel(item)}
        </span>
      ))}
    </div>
  );
};

const RmContentFooter: React.FC<RmContentFooterProps> = ({ pillar, teacher, tags }) => {
  const hasContent = (pillar?.length > 0) || (teacher?.length > 0) || (tags?.length > 0);
  if (!hasContent) return null;

  return (
    <div className="max-w-3xl mx-auto mt-8 pt-5 border-t border-gray-200/80 space-y-3">
      <PillRow
        label="Pillars"
        items={pillar}
        icon={Compass}
        pillClassName="bg-rm-primary-700/10 text-rm-primary-700 border-rm-primary-700/20"
      />
      <PillRow
        label="Teachers"
        items={teacher}
        icon={GraduationCap}
        pillClassName="bg-rm-teal-600/10 text-rm-teal-600 border-rm-teal-600/20"
      />
      <PillRow
        label="Tags"
        items={tags}
        icon={Tag}
        pillClassName="bg-rm-bg-50 text-rm-neutral-600 border-gray-200"
      />
    </div>
  );
};

export default RmContentFooter;
